import { useAuth } from '@clerk/react'
import { useQuery } from '@tanstack/react-query'
import { ArrowRight, Check, Clock3, RotateCcw } from 'lucide-react'
import { useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Header } from '../components/Header'
import { apiRequest } from '../lib/api'
import { useCart } from '../lib/cart'
import type { Order } from '../types'

export function CheckoutResultPage({ outcome }: { outcome: 'success' | 'cancelled' }) {
  const { isSignedIn, getToken } = useAuth()
  const { clear } = useCart()

  useEffect(() => {
    if (outcome === 'success') clear()
  }, [outcome, clear])
  const orders = useQuery({
    queryKey: ['orders', 'checkout-result'],
    queryFn: async () => apiRequest<{ data: Order[] }>('/api/me/orders', { token: await getToken() }),
    enabled: outcome === 'success' && Boolean(isSignedIn),
    refetchInterval: outcome === 'success' ? 3000 : false,
    refetchIntervalInBackground: false,
  })
  const latest = orders.data?.data[0]

  return (
    <div className="page-shell">
      <Header />
      <main className="result-page section-frame">
        <section className="result-card" data-outcome={outcome}>
          {outcome === 'success' ? (
            <>
              <div className="t-success-check" aria-hidden="true"><svg viewBox="0 0 52 52"><circle cx="26" cy="26" r="24" /><path d="M15 27.5 22.5 35 38 18" /></svg><span /></div>
              <span className="eyebrow">CHECKOUT COMPLETE</span>
              <h1>You are in.</h1>
              <p>Stripe confirmed the payment. Your license key is on its way to your inbox and your account page.</p>
              <div className="result-status"><span><i />{latest?.deliveryStatus === 'delivered' ? 'Key delivered' : latest?.status === 'paid' ? 'Preparing your key' : 'Syncing webhook'}</span><small>{latest ? `Order #${latest.id.slice(-8).toUpperCase()}` : 'This usually takes a few seconds'}</small></div>
              <div className="result-actions"><Link className="button button--primary" to="/account">View my account <ArrowRight size={17} /></Link><Link className="button button--ghost" to="/">Back to the market</Link></div>
            </>
          ) : (
            <>
              <div className="result-icon result-icon--cancel"><RotateCcw /></div>
              <span className="eyebrow">CHECKOUT CANCELLED</span>
              <h1>No charges. No problem.</h1>
              <p>No payment was completed. Your reservation will be released automatically when the secure session expires.</p>
              <div className="result-status"><span><Clock3 size={14} /> Temporary hold</span><small>We protect the stock for a few minutes</small></div>
              <div className="result-actions"><Link className="button button--primary" to="/#products">Back to products <ArrowRight size={17} /></Link><Link className="button button--ghost" to="/">Go home</Link></div>
            </>
          )}
          <div className="result-security"><Check size={13} /> Your payment details never touch our servers</div>
        </section>
      </main>
    </div>
  )
}
